import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { 
  Lightbulb, 
  Brain, 
  Users, 
  ShoppingCart, 
  Truck, 
  MessageSquare,
  Zap,
  BarChart3,
  ArrowRight,
  CheckCircle,
  XCircle,
  Target,
  Clock,
  DollarSign,
  TrendingUp,
  Eye,
  Cpu,
  Database,
  Workflow,
  Settings,
  Factory,
  Smartphone,
  Car,
  Wrench,
  Home,
  Gamepad2,
  Sofa,
  Handshake
} from "lucide-react";

export function PLM() {
  const plmModules = [
    {
      icon: Lightbulb,
      title: "Ideation & Market Intelligence",
      description: "Transform ideas into market-ready concepts with AI-powered market research and competitive analysis"
    },
    {
      icon: Settings,
      title: "Design & Engineering",
      description: "Streamline design workflows with intelligent CAD integration and automated design validation"
    },
    {
      icon: Users,
      title: "Vendor & Sourcing",
      description: "AI-driven vendor selection and sourcing optimization for optimal cost and quality balance"
    },
    {
      icon: ShoppingCart,
      title: "Work Order & Procurement",
      description: "Automated procurement processes with intelligent work order management and tracking"
    },
    {
      icon: Truck,
      title: "Supply Chain Orchestration",
      description: "End-to-end supply chain visibility with predictive logistics and inventory optimization"
    },
    {
      icon: MessageSquare,
      title: "Post-Production Feedback",
      description: "Continuous improvement through automated feedback collection and analysis"
    }
  ];

  const aiCapabilities = [
    {
      category: "Intelligent Automation",
      features: [
        "AI-generated BOMs with version control",
        "Automated vendor matching and selection",
        "Intelligent compliance tracking and alerts",
        "Smart workflow orchestration"
      ]
    },
    {
      category: "Predictive Analytics",
      features: [
        "Cost prediction and optimization",
        "Demand forecasting integration",
        "Anomaly detection across processes",
        "Performance trend analysis"
      ]
    }
  ];

  const beforeAfter = {
    before: [
      "Fragmented spreadsheets for BOMs and tracking",
      "Manual approvals and lengthy tracking processes",
      "Poor visibility into supplier performance",
      "Missed market windows due to process delays"
    ],
    after: [
      "AI-generated BOMs & automated version control",
      "SLA-driven maker-checker flows with transparency",
      "Auction-based procurement with full audit trails",
      "Real-time dashboards for time-to-market and cost KPIs"
    ]
  };

  const valueProps = [
    {
      title: "Unified Lifecycle Control",
      description: "Manage every phase from concept to post-production with a single, intelligent platform."
    },
    {
      title: "AI-Enhanced Decisions",
      description: "Automate BOM generation, vendor selection, demand forecasting, and compliance tracking using embedded AI."
    },
    {
      title: "Rapid Deployment",
      description: "Modular and ready-to-integrate with your ERP, CAD, CRM, and collaboration stack."
    },
    {
      title: "Enterprise-Ready",
      description: "Built for scale, security, and performance—tailored for complex manufacturing operations."
    }
  ];

  const results = [
    {
      metric: "75%",
      title: "Faster Time-to-Market",
      description: "Accelerate product development cycles with streamlined workflows and automated processes.",
      icon: Clock
    },
    {
      metric: "50%",
      title: "Reduced Product Costs",
      description: "Optimize material costs and vendor selection through AI-powered procurement and sourcing.",
      icon: DollarSign
    },
    {
      metric: "90%",
      title: "Improved Collaboration",
      description: "Enhanced team collaboration across design, engineering, and supply chain functions.",
      icon: Users
    },
    {
      metric: "24/7",
      title: "Real-time Visibility",
      description: "Complete transparency across your entire product lifecycle and supply chain operations.",
      icon: Eye
    }
  ];

  const industries = [
    {
      category: "Electronics Manufacturing",
      icon: Smartphone,
      sectors: [
        "Consumer electronics and appliances",
        "Mobile devices and telecommunications",
        "Computing hardware and peripherals",
        "Automotive electronics and components"
      ]
    },
    {
      category: "Durable Goods",
      icon: Wrench,
      sectors: [
        "Industrial equipment and machinery",
        "Home and garden products",
        "Sporting goods and recreational equipment",
        "Furniture and fixtures"
      ]
    },
    {
      category: "Contract Manufacturing",
      icon: Factory,
      sectors: [
        "OEM and ODM partnerships",
        "Private label manufacturing",
        "Component suppliers",
        "Assembly and distribution partners"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* The End-to-End ProLiMa Stack */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Built for Every Stage of Your Product Lifecycle</h2>
              <p className="text-lg text-muted-foreground">
                Comprehensive ProLiMa modules that work together seamlessly
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plmModules.map((module, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                        <module.icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-lg">{module.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{module.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI at the Core */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Intelligence That Drives Every Workflow</h2>
              <p className="text-lg text-muted-foreground">
                AI-powered automation and analytics at the heart of your ProLiMa operations
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {aiCapabilities.map((capability, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
                        <Brain className="h-5 w-5 text-white" />
                      </div>
                      <CardTitle className="text-xl">{capability.category}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {capability.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solving Complex Challenges */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">From Chaos to Clarity—Eliminate Bottlenecks at Scale</h2>
              <p className="mb-8 text-lg text-muted-foreground max-w-4xl mx-auto">
                Most enterprises rely on disconnected tools—spreadsheets for BOMs, email for vendors, and legacy ERPs for compliance. ProLiMa centralizes and automates all of it.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
              {/* Before ProLiMa */}
              <div>
                <h3 className="mb-6 text-2xl text-center">Before ProLiMa:</h3>
                <div className="space-y-4">
                  {beforeAfter.before.map((item, index) => (
                    <Card key={index} className="border-destructive/20 bg-destructive/5">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <XCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* With ProLiMa */}
              <div>
                <h3 className="mb-6 text-2xl text-center">With ProLiMa:</h3>
                <div className="space-y-4">
                  {beforeAfter.after.map((item, index) => (
                    <Card key={index} className="border-green-200 bg-green-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-green-800">{item}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            {/* Results You Can Expect */}
            <div className="text-center">
              <h3 className="mb-8 text-2xl">Results You Can Expect:</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">75%</div>
                  <p className="text-sm text-muted-foreground">faster time-to-market</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-secondary mb-2">40%</div>
                  <p className="text-sm text-muted-foreground">reduction in sourcing costs</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-accent mb-2">90%+</div>
                  <p className="text-sm text-muted-foreground">improvement in collaboration</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">100%</div>
                  <p className="text-sm text-muted-foreground">process transparency</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F6FBEA' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Core Value Propositions</h2>
              <p className="text-lg text-muted-foreground">
                Why ProLiMa is the right choice for your product lifecycle management
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {valueProps.map((prop, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-xl">{prop.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{prop.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Proven Results */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Proven Results for Product Teams</h2>
              <p className="text-lg text-muted-foreground">
                Measurable impact across your entire product development lifecycle
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {results.map((result, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow text-center">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-center mb-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
                        <result.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className="text-4xl lg:text-5xl font-bold text-primary mb-3">
                      {result.metric}
                    </div>
                    <h3 className="text-xl mb-3">{result.title}</h3>
                    <p className="text-muted-foreground">{result.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Industries We Serve</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                ProLiMa is specifically designed for electronics and durable goods manufacturers who need comprehensive product lifecycle management
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {industries.map((industry, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: '#819A40' }}>
                        <industry.icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{industry.category}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {industry.sectors.map((sector, sectorIndex) => (
                        <li key={sectorIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{sector}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Success Stories */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl font-bold">Customer Success Stories</h2>
            <p className="mb-8 text-lg text-muted-foreground">
              See how leading manufacturers are transforming their product lifecycle with ProLiMa
            </p>
            
            <Card className="mb-8">
              <CardContent className="p-8">
                <div className="flex items-center justify-center mb-6">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary/10">
                    <Handshake className="h-8 w-8 text-secondary" />
                  </div>
                </div>
                <blockquote className="text-lg text-muted-foreground italic mb-4">
                  "ProLiMa transformed our product development process. We reduced time-to-market by 60% and improved cross-team collaboration dramatically. The AI-powered BOM generation alone saved us hundreds of hours per project."
                </blockquote>
                <div className="text-sm">
                  <div className="font-semibold">Sarah Chen</div>
                  <div className="text-muted-foreground">VP of Product Development, TechCorp Manufacturing</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl text-white font-bold">
              Ready to Transform Your Product Lifecycle?
            </h2>
            <p className="mb-8 text-lg text-white/90">
              Join leading manufacturers who trust ProLiMa to accelerate their product development and streamline operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group bg-white text-primary hover:bg-gray-100">
                Request a Demo
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" className="bg-white text-primary border-white hover:bg-gray-100">
                <Settings className="mr-2 h-4 w-4" />
                Explore Features
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}